import { makeClassName } from '@siteless/util';
import { uniq } from 'lodash';
import Page from './Page';
import GroupIcon from './GroupIcon';
import GlobeIcon from './GlobeIcon';
import Action from './Action';
import PlusIcon from './PlusIcon';
import SettingsIcon from './SettingsIcon';
import useCopy from '../util/useCopy';
import { OneLeagueResourceType } from '../resources/League/One';

export const LeagueBarCountMode = {
  members: 'members',
  completedBallots: 'completedBallots',
} as const;

type CountMode = (typeof LeagueBarCountMode)[keyof typeof LeagueBarCountMode];

type LeagueBarProps = {
  leagues: NonNullable<OneLeagueResourceType>[];
  completedBallots: Record<string, boolean>;
  focusedLeagueId?: string;
  onSelectLeague: (leagueId: string | undefined) => void;
  countMode: CountMode;
  selectable?: boolean;
  disableEveryone?: boolean;
  showCreateButton?: boolean;
  onCreateLeague?: () => void;
  currentUserId?: string;
  onOpenSettings?: (leagueId: string) => void;
  fullScreen?: boolean;
  className?: string;
};

let LeagueBar = ({
  leagues,
  completedBallots,
  focusedLeagueId,
  onSelectLeague,
  countMode,
  selectable,
  disableEveryone,
  showCreateButton,
  onCreateLeague,
  currentUserId,
  onOpenSettings,
  fullScreen,
  className,
}: LeagueBarProps) => {
  let copy = useCopy();

  let getLeagueCount = (league: NonNullable<OneLeagueResourceType>) =>
    countMode === 'members'
      ? league.memberUserIds.length
      : uniq([league.ownerUserId, ...league.memberUserIds]).filter(
          (userId) => completedBallots[userId],
        ).length;

  let makeChipClass = (active: boolean) =>
    makeClassName(
      'flex flex-none cursor-pointer items-center rounded-2xl border-4 pl-4 py-1.5 font-medium shadow-md',
      !selectable || active
        ? 'bg-neutral-0 border-neutral-200 text-rose-dark'
        : 'border-rose-dark bg-rose-500 text-neutral-100',
    );

  let accentClass = (active: boolean) =>
    !selectable || active ? 'text-rose-500' : '';

  let isEveryoneActive = !focusedLeagueId;

  return (
    <Page.Bar className={className} fullScreen={fullScreen}>
      {Boolean(showCreateButton) && (
        <div className="mr-2 flex h-full items-center rounded-2xl bg-purple-900 px-2">
          <Action
            Icon={PlusIcon}
            className="whitespace-nowrap pl-0 text-sm"
            onClick={onCreateLeague}
          >
            {leagues.length > 0 ? copy.Create : copy.newLeague}
          </Action>
        </div>
      )}
      {!disableEveryone && (
        <div
          className={makeClassName(
            'mr-2 pr-4',
            makeChipClass(isEveryoneActive),
          )}
          onClick={() => onSelectLeague(undefined)}
        >
          <GlobeIcon
            className={makeClassName(
              'mr-1 h-4 w-4 flex-none',
              accentClass(isEveryoneActive),
            )}
          />
          <div
            className={makeClassName(
              'mr-2 flex-none font-normal',
              accentClass(isEveryoneActive),
            )}
          >
            {Object.keys(completedBallots).length}
          </div>
          <div className="truncate">{copy.everyone}</div>
        </div>
      )}
      {leagues.map((league) => {
        let active = focusedLeagueId === league.id;
        let isOwner = onOpenSettings && currentUserId === league.ownerUserId;
        return (
          <div
            key={league.id}
            className={makeClassName(
              'mr-2',
              makeChipClass(active),
              isOwner ? 'pr-3' : 'pr-4',
            )}
            onClick={() => onSelectLeague(league.id)}
          >
            <GroupIcon
              className={makeClassName(
                'mr-1 h-4 w-4 flex-none',
                accentClass(active),
              )}
            />
            <div
              className={makeClassName(
                'mr-2 flex-none font-normal',
                accentClass(active),
              )}
            >
              {getLeagueCount(league)}
            </div>
            <div
              className={makeClassName(
                'truncate',
                isOwner ? 'max-w-[28vw]' : 'max-w-[34vw]',
              )}
            >
              {league.name}
            </div>
            {isOwner && onOpenSettings && (
              <div
                className={makeClassName(
                  'ml-2 flex-none border-l pl-2',
                  !selectable || active
                    ? 'border-neutral-200 text-neutral-400'
                    : 'border-rose-300/40 text-rose-300',
                )}
              >
                <SettingsIcon
                  className="h-3.5 w-3.5"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onSelectLeague(league.id);
                    onOpenSettings(league.id);
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
      <div className="h-2.5 w-2.5 flex-none" />
    </Page.Bar>
  );
};

export default LeagueBar;
